import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { appeler, ErreurApi } from "../api/client";

/** Doit rester égal à `IdentifiantsPortail.LongueurMinimale` côté API. */
const LONGUEUR_MINIMALE = 12;

const ReponseSchema = z.object({
  identifiant: z.string(),
  creation: z.boolean(),
  note: z.string(),
});

/**
 * §5.A — Établir ou réinitialiser les accès d'un client au portail.
 *
 * ⛔ C'EST LA SEULE VOIE DE RÉINITIALISATION.
 *
 * Réémettre un lien ne refait plus les identifiants : un lien capable de
 * remplacer un mot de passe redeviendrait un porteur, et c'est précisément ce
 * qu'on a cessé d'accepter. Un mot de passe perdu se rejoue donc ici, par un
 * acte du gérant — identifié, tracé, réservé à l'admin.
 */
export function DialogueAcces({
  dossier,
  onFerme,
  onFait,
}: {
  dossier: { id: string; raisonSociale: string; identifiantPortail: string | null };
  onFerme: () => void;
  onFait: (note: string) => void;
}) {
  const reinitialisation = dossier.identifiantPortail !== null;

  const [identifiant, setIdentifiant] = useState(dossier.identifiantPortail ?? "");
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const dialogue = useRef<HTMLDialogElement>(null);

  // `showModal` — et non l'attribut `open` — est ce qui donne le piégeage du
  // clavier, la fermeture par Échap et l'inertie du fond. Reproduire tout cela
  // à la main se fait mal, et s'oublie toujours quelque part.
  useEffect(() => dialogue.current?.showModal(), []);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);

    // Vérifiée ici et nulle part ailleurs : elle ne protège pas d'un attaquant,
    // elle protège d'une faute de frappe. L'envoyer au serveur n'apporterait
    // rien et ferait un secret de plus à transporter.
    if (motDePasse !== confirmation) {
      setErreur("Les deux mots de passe ne sont pas identiques.");
      return;
    }

    setEnCours(true);
    try {
      const r = await appeler(`/dossiers/${dossier.id}/acces`, ReponseSchema, {
        method: "PUT",
        body: JSON.stringify({ identifiant, motDePasse }),
      });
      onFait(`${r.note} Identifiant : ${r.identifiant}`);
    } catch (e) {
      setErreur(
        e instanceof ErreurApi
          ? [e.message, e.motif].filter(Boolean).join(" ")
          : "Enregistrement impossible.",
      );
    } finally {
      setEnCours(false);
    }
  }

  return (
    <dialog ref={dialogue} className="dialogue" onClose={onFerme}>
      {/* Pas de `method="dialog"` : il ferme la boite AU MOMENT du submit,
          donc avant que l'appel reseau ait repondu — l'utilisateur ne verrait
          ni l'erreur, ni la confirmation. */}
      <form onSubmit={soumettre}>
        <h2>
          {reinitialisation ? "Réinitialiser les accès" : "Créer les accès"} —{" "}
          {dossier.raisonSociale}
        </h2>

        <p className="resume">
          {reinitialisation ? (
            <>
              Le client se connecte aujourd'hui avec{" "}
              <strong>{dossier.identifiantPortail}</strong>. Enregistrer un
              nouveau mot de passe <strong>annule l'ancien</strong> et révoque
              tout lien encore en circulation.
            </>
          ) : (
            <>
              Ces identifiants remplacent le lien : le client s'en servira à
              chaque connexion. Transmettez-les-lui par un canal sûr — et de
              préférence pas par le même que le lien.
            </>
          )}
        </p>

        <label>
          Identifiant
          <input
            value={identifiant}
            onChange={(e) => setIdentifiant(e.target.value)}
            autoComplete="off"
            placeholder="Ex. globe.ci"
            minLength={3}
            required
          />
        </label>
        <label>
          Mot de passe
          <input
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete="new-password"
            minLength={LONGUEUR_MINIMALE}
            required
          />
        </label>
        <label>
          Confirmez le mot de passe
          <input
            type="password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="new-password"
            minLength={LONGUEUR_MINIMALE}
            required
          />
        </label>

        <p className="note">
          Au moins {LONGUEUR_MINIMALE} caractères, et le mot de passe ne doit pas
          contenir l'identifiant. Une phrase de trois mots convient.
        </p>

        {erreur && (
          <p className="alerte" role="alert">
            {erreur}
          </p>
        )}

        <div className="dialogue__actions">
          <button type="button" onClick={() => dialogue.current?.close()}>
            Annuler
          </button>
          <button type="submit" disabled={enCours}>
            {enCours ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
